import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { CalendarIcon, MapPin, Clock, Users } from "lucide-react";
import { format } from "date-fns";

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  const display = `${String(h).padStart(2, "0")}:${m}`;
  return display;
});

export default function BookingWidget({ compact = false }) {
  const navigate = useNavigate();
  const [rideType, setRideType] = useState("one-way");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("10:00");
  const [passengers, setPassengers] = useState(1);
  const [hours, setHours] = useState(3);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set("ride_type", rideType);
    params.set("pickup", pickup);
    if (rideType !== "hourly") params.set("dropoff", dropoff);
    if (date) params.set("date", date.toISOString());
    params.set("time", time);
    params.set("passengers", String(passengers));
    if (rideType === "hourly") params.set("hours", String(hours));
    navigate(`/book?${params.toString()}`);
  };

  return (
    <form
      data-testid="booking-widget"
      onSubmit={submit}
      className={`backdrop-blur-xl bg-black/60 border border-white/10 ${compact ? "p-6" : "p-8 md:p-10"} shadow-2xl`}
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-5">Reserve Your Ride</div>

      <Tabs value={rideType} onValueChange={setRideType} className="mb-6">
        <TabsList data-testid="ride-type-tabs" className="bg-transparent border border-white/10 rounded-none p-0 h-auto w-full grid grid-cols-3">
          <TabsTrigger
            data-testid="tab-one-way"
            value="one-way"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-[11px] uppercase tracking-[0.2em] py-3"
          >
            One-Way
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-hourly"
            value="hourly"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-[11px] uppercase tracking-[0.2em] py-3"
          >
            By the Hour
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-round-trip"
            value="round-trip"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-[11px] uppercase tracking-[0.2em] py-3"
          >
            Round Trip
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Pickup Location</label>
          <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#C5A059] transition-colors py-2">
            <MapPin className="w-4 h-4 text-[#C5A059]" />
            <input
              data-testid="booking-pickup-input"
              required
              value={pickup}
              onChange={(e) => setPickup(e.target.value)}
              placeholder="JFK Terminal 4"
              className="bg-transparent text-white outline-none w-full placeholder:text-white/30"
            />
          </div>
        </div>

        {rideType !== "hourly" ? (
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Drop-off Location</label>
            <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#C5A059] transition-colors py-2">
              <MapPin className="w-4 h-4 text-[#C5A059]" />
              <input
                data-testid="booking-dropoff-input"
                required
                value={dropoff}
                onChange={(e) => setDropoff(e.target.value)}
                placeholder="The Plaza Hotel"
                className="bg-transparent text-white outline-none w-full placeholder:text-white/30"
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Duration (Hours)</label>
            <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#C5A059] transition-colors py-2">
              <Clock className="w-4 h-4 text-[#C5A059]" />
              <input
                data-testid="booking-hours-input"
                type="number"
                min={2}
                max={24}
                value={hours}
                onChange={(e) => setHours(Math.max(2, Number(e.target.value)))}
                className="bg-transparent text-white outline-none w-full"
              />
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Pickup Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="booking-date-trigger"
                type="button"
                className="w-full flex items-center gap-2 border-b border-white/20 hover:border-[#C5A059] transition-colors py-2 text-left"
              >
                <CalendarIcon className="w-4 h-4 text-[#C5A059]" />
                <span className={date ? "text-white" : "text-white/30"}>
                  {date ? format(date, "PPP") : "Select date"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="bg-[#141414] border-white/10 p-0 rounded-none" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className="bg-[#141414] text-white"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Pickup Time</label>
          <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#C5A059] transition-colors py-2">
            <Clock className="w-4 h-4 text-[#C5A059]" />
            <select
              data-testid="booking-time-select"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="bg-transparent text-white outline-none w-full appearance-none cursor-pointer"
            >
              {TIME_OPTIONS.map((t) => (
                <option key={t} value={t} className="bg-[#141414]">{t}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Passengers</label>
          <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#C5A059] transition-colors py-2">
            <Users className="w-4 h-4 text-[#C5A059]" />
            <input
              data-testid="booking-passengers-input"
              type="number"
              min={1}
              max={8}
              value={passengers}
              onChange={(e) => setPassengers(Math.max(1, Number(e.target.value)))}
              className="bg-transparent text-white outline-none w-full"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        data-testid="booking-widget-submit"
        className="mt-8 w-full bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-[0.3em] font-medium"
      >
        Book & Reserve
      </button>
    </form>
  );
}
